import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

export default function Header() {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const headerRef = useRef(null);

  // Close menu on route change (ensures no stale open state after navigation)
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  // Scroll lock when menu open
  useEffect(() => {
    if (isMenuOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [isMenuOpen]);

  // Outside click & ESC handling
  useEffect(() => {
    if (!isMenuOpen) return;
    function handleKey(e) { if (e.key === 'Escape') setIsMenuOpen(false); }
    function handleClick(e) { if (panelRef.current && !panelRef.current.contains(e.target) && buttonRef.current && !buttonRef.current.contains(e.target)) setIsMenuOpen(false); }
    window.addEventListener('keydown', handleKey);
    window.addEventListener('mousedown', handleClick);
    return () => { window.removeEventListener('keydown', handleKey); window.removeEventListener('mousedown', handleClick); };
  }, [isMenuOpen]);

  // Keep a CSS variable updated with the header's height so sticky bars can sit just below it
  useEffect(() => {
    if (!headerRef.current) return;
    const el = headerRef.current;
    const setVar = () => {
      const h = el.offsetHeight || 0;
      document.documentElement.style.setProperty('--header-h', `${h}px`);
    };
    setVar();
    const ro = new ResizeObserver(() => setVar());
    ro.observe(el);
    window.addEventListener('load', setVar);
    window.addEventListener('resize', setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener('load', setVar);
      window.removeEventListener('resize', setVar);
    };
  }, []);

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/about', label: t('nav.about') },
    { href: '/services', label: t('nav.services') },
    { href: '/offers', label: t('nav.offers'), highlight: true },
    { href: '/blog', label: t('nav.blog') },
    { href: '/testimonials', label: t('nav.testimonials') },
    { href: '/request-quote', label: t('nav.requestQuote') },
    { href: '/contact', label: t('nav.contact') },
  ];

  // Helper to check if a link is active
  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
  <header ref={headerRef} className="w-full sticky top-0 z-20 bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 shadow-lg border-b border-blue-800/60 backdrop-blur supports-[backdrop-filter]:bg-blue-900/95">
  <div className="max-w-7xl mx-auto px-4 py-2 md:py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center group" aria-label="Vigor Auto Repair Home">
          <img
            src="/logo3.png"
            alt="Vigor Auto Repair"
            className="h-14 w-auto md:h-16 xl:h-20 drop-shadow-sm transition-transform duration-300 group-hover:scale-[1.02] group-active:scale-[0.98]"
            loading="eager"
            decoding="async"
            style={{maxWidth: '100%', objectFit: 'contain'}}
          />
        </Link>
        
        {/* Desktop Nav */}
        <nav className="space-x-6 text-sm font-medium hidden sm:flex items-center">
          {navLinks.map(link => {
            const active = isActive(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`tracking-wide relative py-1 transition-all duration-300 ${
                  active 
                    ? 'text-[#F5D313] font-semibold after:w-full' 
                    : 'text-slate-200 hover:text-[#F5D313] after:w-0 hover:after:w-full'
                } after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:bg-[#F5D313] after:transition-all after:duration-300 after:rounded-full`}
              >
                {link.label}
              </Link>
            );
          })}
          <div className="ml-4">
            <LanguageSwitcher />
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <div className="sm:hidden">
          <button
            ref={buttonRef}
            onClick={() => setIsMenuOpen(o => !o)}
            className="text-slate-200 hover:text-[#F5D313] focus:outline-none p-2 rounded-md focus-visible:ring-2 focus-visible:ring-[#F5D313]"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav-panel"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M3 6h18M3 12h18M8 18h13'} />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Panel (animated) */}
      <div
        id="mobile-nav-panel"
        ref={panelRef}
  className={`sm:hidden overflow-hidden origin-top transition-[max-height,opacity,transform] duration-300 ease-out ${isMenuOpen ? 'max-h-[600px] opacity-100 scale-y-100' : 'max-h-0 opacity-0 scale-y-95'} bg-blue-950/95 backdrop-blur border-t border-blue-800`}
        aria-hidden={!isMenuOpen}
      >
        <nav className="flex flex-col py-3 px-4 divide-y divide-slate-700/70">
          {navLinks.map((link) => {
            const active = isActive(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsMenuOpen(false)}
                className={`flex items-center justify-between gap-4 py-3 text-base font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5D313] focus-visible:ring-offset-2 focus-visible:ring-offset-blue-900 ${
                  active
                    ? 'text-[#F5D313] font-semibold'
                    : 'text-slate-200 hover:text-[#F5D313]'
                }`}
              >
                <span className="flex items-center gap-2">
                  {active && (
                    <span className="h-1.5 w-1.5 rounded-full bg-[#F5D313] animate-pulse" />
                  )}
                  {link.label}
                </span>
                <svg className={`w-4 h-4 transition-colors ${active ? 'text-[#F5D313]' : 'text-slate-500'}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </Link>
            );
          })}
          <div className="pt-4 flex flex-col gap-3">
            <div className="flex justify-center">
              <LanguageSwitcher />
            </div>
            <a
              href="https://wa.me/971524333690"
              className="inline-flex items-center justify-center rounded-lg bg-[#F5D313] text-slate-900 font-semibold py-2.5 text-sm shadow hover:bg-[#e4c40f] transition"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.whatsapp')}
            </a>
            <Link
              to="/request-quote"
              className="inline-flex items-center justify-center rounded-lg border border-slate-600 text-slate-200 font-semibold py-2.5 text-sm hover:bg-slate-800 transition"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.getQuote')}
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}
